import { Button, Icon, IconSize } from "@blueprintjs/core";
import React, { useCallback, useEffect } from "react";
import { useParams } from "react-router-dom";

import { usePlayContextApi } from "../../pages/Play/hooks/use-play-context-api";
import { PlayContext } from "../../pages/Play/play-context";
import { useGetSinglePlay } from "../../redux/slices";
import { PlayRouteParams, SportPlay } from "../../types";
import { getOutofBounds } from "../../utils";
import { TIMELINE_VIEW } from "../animation-timeline";
import { LoadingPage } from "../loading-page/loading-page";
import { PlayingSurface } from "../playing-surface/playing-surface";
import { PlayControl } from "./play-control";
import styles from "./read-only-play.module.scss";

export interface ReadOnlyPlayProps {
  interactive?: boolean;
  playOnHover?: boolean;
}

export const ReadOnlyPlay = ({ interactive = false }: ReadOnlyPlayProps) => {
  const { frameIndex, playId } = useParams<PlayRouteParams>();
  const { data: play, isFetching } = useGetSinglePlay(playId);

  if (isFetching || !play) return <LoadingPage />;

  return (
    <ReadOnlyPlayComponent
      play={play}
      frameIndex={frameIndex ? parseInt(frameIndex) : undefined}
      interactive={interactive}
    />
  );
};

export interface ReadOnlyPlayComponentProps<TPlay> extends ReadOnlyPlayProps {
  play: TPlay;
  frameIndex?: number;
}

export const ReadOnlyPlayComponent = <TPlay extends SportPlay>({
  play,
  frameIndex,
  interactive = false,
  playOnHover = false,
}: ReadOnlyPlayComponentProps<TPlay>) => {
  /** ======== Play Context API ======== */
  const playContextApi = usePlayContextApi(play);
  const { playStateApi, animationTimerApi, drawToolbarApi } = playContextApi;

  const { playState, activeFrame, activeFrameStencils, setActiveFrameId } = playStateApi;
  const { animationView } = drawToolbarApi;
  const { playTimer, pauseTimer, resetTimer, setTime, isPlaying, setTimelineView, time, maxTime } = animationTimerApi;

  useEffect(() => {
    if (!animationView.isActive) {
      animationView.toggle();
    }
  }, [animationView]);

  const activeFrameId = activeFrame?.id;
  useEffect(() => {
    if (frameIndex !== undefined) {
      setTimelineView(TIMELINE_VIEW.FRAME);

      const frameId = play.frames[frameIndex]?.id;
      if (frameId && activeFrameId !== frameId) {
        setActiveFrameId(frameId);
      }
    } else {
      setTimelineView(TIMELINE_VIEW.ALL);
    }
  }, [setTimelineView, frameIndex, setActiveFrameId, activeFrameId, play]);

  const playControllerClickHandler = useCallback(() => {
    if (isPlaying) pauseTimer();
    else {
      if (time >= maxTime) resetTimer();
      playTimer();
    }
  }, [isPlaying, maxTime, time, pauseTimer, playTimer, resetTimer]);

  const handleMouseEnter = () => {
    if (playOnHover) playTimer();
  };

  const handleMouseLeave = () => {
    if (playOnHover) {
      pauseTimer();
      resetTimer();
    }
  };

  return (
    <div className={styles.readOnlyContainer} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <PlayContext.Provider value={playContextApi}>
        <Button
          className={styles.playButton}
          icon={<Icon icon="play" size={IconSize.LARGE} />}
          minimal
          small
          onClick={playTimer}
          id="readonly-play-button"
        />
        <PlayingSurface
          play={playState}
          stencils={activeFrameStencils}
          outOfBounds={getOutofBounds(playState)}
          isEditable
        />
        {interactive && (
          <div className={styles.playOverlay}>
            {!playOnHover && (
              <>
                <div className={styles.playController} onClick={playControllerClickHandler}>
                  {!isPlaying && (time === 0 || time >= maxTime) && (
                    <>
                      <div className={styles.opaqueCover} />
                      <Icon className={styles.playOverlayButton} color="#fff" icon="play" size={300} />
                    </>
                  )}
                </div>
                <PlayControl time={time} maxTime={maxTime} setTime={setTime} pause={pauseTimer} />
              </>
            )}
          </div>
        )}
      </PlayContext.Provider>
    </div>
  );
};
